import type { PlanPoint, PlanRoom } from "@sts/geometry";
import {
  PURMO_COMPACT,
  RADIATOR_PLAN_RANGE,
  radiatorLabel,
  radiatorSpecFor,
  type PlacedGeometry,
  type RadiatorCatalogueEntry,
  type RadiatorSpec,
} from "@sts/rules-data";
import {
  formatC,
  formatPercent,
  formatW,
  type CheckState,
  type DesignCheckHeatLoss,
  type DesignCheckHeatLossRoom,
} from "./design-check-types.js";
import { correctEmitterOutput, flowTempForRequiredOutput } from "./emitters.js";
import { buildFitScene, fittingPlacement, insideConvex, type FitScene } from "./plan-fit.js";
import { wallsFacingRooms, type PlanObjectOnPlan } from "./plan-geometry.js";

/**
 * Do the radiators on the plan heat their rooms? — the red/green half of the
 * design check Josh asked for on 2026-09-10: "it must turn red if we recommend
 * smaller radiator that does not fit according to mcs requirements".
 *
 * THE RULE. Each room's heat loss (the engine's BS EN 12831 figure, MIS
 * 3005-D §3.4.1(a)) against the summed output of the radiators drawn in it,
 * each corrected from its catalogue rating to the design water temperatures:
 *
 *   output = rated(ΔT50) × ((flow + return)/2 − room design temp) / 50)^n
 *
 * (`correctEmitterOutput`; rated output, rating ΔT and exponent n all from the
 * Purmo Compact catalogue the plan's radiators are drawn from). 100% or more
 * passes; anything less fails — no tolerance, because a tolerance is the
 * designer's to sign, not the drawing's to apply. MIS 3005-D §3.8.1(c) wants
 * emitter types and dimensions sized for the design, and §3.8.1(d) the design
 * emitter temperature set by the worst performing room, which this reports.
 *
 * WHICH ROOM A RADIATOR IS IN. The scanned room whose floor outline holds its
 * centre; failing that (a floor RoomPlan did not capture) the room of the wall
 * its back is against. Which HEAT-LOSS room that scanned room is: the survey
 * room recorded with the scanned room's identity (`externalRef`, written by
 * `convertCapture`) — never matched by name or position. A scanned room with
 * no such survey room has no heat loss, and says so in grey; nothing is
 * estimated from the scan.
 */

/** The plan's radiators are Purmo Compact sizes; their outputs come from the same rows. */
const CATALOGUE = PURMO_COMPACT;

export function catalogueEntryFor(
  spec: Pick<RadiatorSpec, "panelType" | "heightMm" | "lengthMm">,
): RadiatorCatalogueEntry | undefined {
  return CATALOGUE.data.entries.find(
    (e) => e.panelType === spec.panelType && e.heightMm === spec.heightMm && e.lengthMm === spec.lengthMm,
  );
}

/** "Purmo Compact catalogue, July 2024 (provisional — see its provenance note)" */
export const RADIATOR_OUTPUT_SOURCE =
  `${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range} catalogue (${CATALOGUE.source.issue}), ` +
  `ΔT50 outputs and exponents — ${CATALOGUE.provenance === "provisional" ? "provisional: transcribed once, not yet independently re-checked" : "verified"}`;

type Radiator = Extract<PlanObjectOnPlan, { kind: "radiator" }>;

export interface DesignWaterTemps {
  readonly flowC: number;
  readonly returnC: number;
  readonly systemDeltaTK: number;
  /** True when ΔT is the design pack's 5 K rather than the designer's own figure. */
  readonly deltaTAssumed: boolean;
}

export interface RadiatorOutput {
  readonly objectId: string;
  /** "K2 600×1200" */
  readonly label: string;
  readonly catalogueId?: string;
  readonly ratedOutputW?: number;
  readonly ratedDeltaTK?: number;
  readonly exponent?: number;
  /** At the design flow/return and the room's design temperature, W. Undefined when it cannot be worked out. */
  readonly outputW?: number;
  readonly working: readonly string[];
  /** Why there is no output figure, when there is none. */
  readonly unknownReason?: string;
}

export interface RadiatorUpgrade {
  /** The radiator to change. */
  readonly objectId: string;
  readonly spec: RadiatorSpec;
  /** Where it goes: passes every fit check (`plan-fit.ts`). */
  readonly geometry: PlacedGeometry;
  /** "Use K2 600×1400 — room 1,720 W, 106.8%, fits" */
  readonly label: string;
  readonly outputW: number;
  readonly roomTotalW: number;
  readonly fraction: number;
}

export interface SingleRadiatorOption {
  readonly spec: RadiatorSpec;
  readonly label: string;
  readonly outputW: number;
}

export interface RoomDesignCheck {
  /** The scanned room. */
  readonly story: number;
  readonly index: number;
  readonly scanLabel: string;
  readonly externalRef?: string;
  /** The heat-loss room it is, when there is one. */
  readonly heatLossRoom?: DesignCheckHeatLossRoom;
  /** What the room is called in messages: the survey room's name, else the scan's "Room 2". */
  readonly name: string;
  readonly state: CheckState;
  /** One line, with its figures. */
  readonly message: string;
  /** Further lines: why, and what could not be checked. */
  readonly notes: readonly string[];
  readonly radiators: readonly RadiatorOutput[];
  readonly totalOutputW?: number;
  /** Output ÷ heat loss; 1 = exactly 100%. */
  readonly fraction?: number;
  readonly shortfallW?: number;
  /** The lowest flow temperature at which these radiators cover the room, °C (MIS 3005-D §3.8.1(d)). */
  readonly requiredFlowTempC?: number;
  /** Bigger radiators that both meet the heat loss and fit where they are. */
  readonly upgrades: readonly RadiatorUpgrade[];
  /** When no bigger size fits: what adding a radiator must make up. */
  readonly addRadiatorNote?: string;
  /** The smallest single catalogue radiator that would cover the room at the design temperatures (not placed). */
  readonly smallestSingle?: SingleRadiatorOption;
}

export interface RoomsCheck {
  readonly rooms: readonly RoomDesignCheck[];
  /** Radiators on the plan that are in no scanned room. */
  readonly unplaced: readonly { readonly objectId: string; readonly label: string }[];
  /** MIS 3005-D §3.8.1(d): the room needing the hottest water, when every assessed room could be. */
  readonly worstRoom?: {
    readonly name: string;
    readonly story: number;
    readonly index: number;
    readonly requiredFlowTempC: number;
    /** False when some rooms could not be assessed, so a worse one may exist. */
    readonly complete: boolean;
  };
}

// ------------------------------------------------------------- arithmetic --

/**
 * The lowest flow temperature at which a set of radiators delivers `loadW`
 * into a room at `roomTempC`, with flow − return = `systemDeltaTK`.
 *
 * One radiator is `flowTempForRequiredOutput`, the engine's own inverse. For
 * several, with different exponents, Σ rated_i × (ΔT/ΔT_rated,i)^n_i = load
 * has no closed form, so it is solved by bisection on the mean-water ΔT —
 * the sum rises strictly with ΔT, so there is exactly one answer — to 1e-6 K.
 */
export function flowTempForRadiators(
  radiators: readonly { readonly ratedOutputW: number; readonly ratedDeltaTK: number; readonly exponent: number }[],
  loadW: number,
  roomTempC: number,
  systemDeltaTK: number,
): number | undefined {
  const live = radiators.filter((r) => r.ratedOutputW > 0);
  if (live.length === 0 || !(systemDeltaTK > 0)) return undefined;
  if (!(loadW > 0)) return roomTempC + systemDeltaTK / 2;
  if (live.length === 1) {
    const r = live[0]!;
    return flowTempForRequiredOutput({
      requiredOutputW: loadW,
      ratedOutputW: r.ratedOutputW,
      roomDesignTempC: roomTempC,
      exponent: r.exponent,
      ratedDeltaTK: r.ratedDeltaTK,
      systemDeltaTK,
    }).requiredFlowTempC;
  }
  const output = (dt: number): number =>
    live.reduce((sum, r) => sum + r.ratedOutputW * (dt / r.ratedDeltaTK) ** r.exponent, 0);
  let lo = 0;
  let hi = 1;
  while (output(hi) < loadW && hi < 1e4) hi *= 2;
  for (let i = 0; i < 200 && hi - lo > 1e-9; i += 1) {
    const mid = (lo + hi) / 2;
    if (output(mid) < loadW) lo = mid;
    else hi = mid;
  }
  return roomTempC + hi + systemDeltaTK / 2;
}

function radiatorOutput(radiator: Radiator, temps: DesignWaterTemps, roomTempC: number): RadiatorOutput {
  const label = radiatorLabel(radiator.spec);
  const entry = catalogueEntryFor(radiator.spec);
  if (!entry) {
    return {
      objectId: radiator.id,
      label,
      working: [],
      unknownReason: `${label} is not a size in the ${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range} catalogue, so its output is unknown.`,
    };
  }
  if (entry.exponent === undefined) {
    return {
      objectId: radiator.id,
      label,
      catalogueId: entry.id,
      ratedOutputW: entry.ratedOutputW,
      ratedDeltaTK: entry.ratedDeltaTK,
      working: [],
      unknownReason: `The catalogue gives no exponent for ${label}, so its output at heat pump temperatures is not worked out (no default is assumed).`,
    };
  }
  const corrected = correctEmitterOutput({
    ratedOutputW: entry.ratedOutputW,
    flowTempC: temps.flowC,
    returnTempC: temps.returnC,
    roomDesignTempC: roomTempC,
    exponent: entry.exponent,
    ratedDeltaTK: entry.ratedDeltaTK,
  });
  return {
    objectId: radiator.id,
    label,
    catalogueId: entry.id,
    ratedOutputW: entry.ratedOutputW,
    ratedDeltaTK: entry.ratedDeltaTK,
    exponent: entry.exponent,
    outputW: corrected.outputW,
    working: [
      `${label}: ${formatW(entry.ratedOutputW)} at ΔT${entry.ratedDeltaTK} (${entry.ratingBasis}), n = ${entry.exponent} — ${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range}, ${entry.partNumber ?? entry.id}`,
      ...corrected.working.map((line) => `  ${line}`),
    ],
  };
}

function outputOf(entry: RadiatorCatalogueEntry, temps: DesignWaterTemps, roomTempC: number): number | undefined {
  if (entry.exponent === undefined) return undefined;
  return correctEmitterOutput({
    ratedOutputW: entry.ratedOutputW,
    flowTempC: temps.flowC,
    returnTempC: temps.returnC,
    roomDesignTempC: roomTempC,
    exponent: entry.exponent,
    ratedDeltaTK: entry.ratedDeltaTK,
  }).outputW;
}

// ------------------------------------------------------ room assignment --

/**
 * The scanned room a radiator is in: the one whose floor outline holds its
 * centre, else the room of the wall its back is against (the nearest wall on
 * whose room side it stands, within its own depth). Undefined when neither.
 */
export function roomOfRadiator(radiator: Radiator, rooms: readonly PlanRoom[]): PlanRoom | undefined {
  const onStorey = rooms.filter((room) => room.story === radiator.storey);
  const centre: PlanPoint = { x: radiator.geometry.x, y: radiator.geometry.y };
  const holding = onStorey.find((room) => room.outlines.some((outline) => insideConvex(centre, outline)));
  if (holding) return holding;

  const reach = radiator.spec.depthMm / 1000;
  let best: { room: PlanRoom; distance: number } | undefined;
  for (const room of onStorey) {
    for (const wall of wallsFacingRooms([room])) {
      const dx = wall.end.x - wall.start.x;
      const dy = wall.end.y - wall.start.y;
      const length = Math.hypot(dx, dy);
      const along = ((centre.x - wall.start.x) * dx + (centre.y - wall.start.y) * dy) / length;
      if (along < 0 || along > length) continue;
      const side = (centre.x - wall.start.x) * wall.inward.x + (centre.y - wall.start.y) * wall.inward.y;
      if (side < -0.01 || side > reach + 0.01) continue;
      if (!best || side < best.distance) best = { room, distance: side };
    }
  }
  return best?.room;
}

// ----------------------------------------------------------------- check --

export interface RoomsCheckInput {
  readonly rooms: readonly PlanRoom[];
  readonly objects: readonly PlanObjectOnPlan[];
  readonly heatLoss: DesignCheckHeatLoss;
  /** Undefined until the designer sets a design flow temperature. */
  readonly temps: DesignWaterTemps | undefined;
  /** How many bigger sizes to offer per short room. */
  readonly maxUpgrades?: number;
}

const roomKey = (story: number, index: number): string => `${story}:${index}`;
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

export function checkRooms(input: RoomsCheckInput): RoomsCheck {
  const { rooms, objects, heatLoss, temps } = input;
  const maxUpgrades = input.maxUpgrades ?? 3;
  const radiators = objects.filter((o): o is Radiator => o.kind === "radiator");

  const inRoom = new Map<string, Radiator[]>();
  const unplaced: { objectId: string; label: string }[] = [];
  for (const radiator of radiators) {
    const room = roomOfRadiator(radiator, rooms);
    if (!room) {
      unplaced.push({ objectId: radiator.id, label: radiatorLabel(radiator.spec) });
      continue;
    }
    const key = roomKey(room.story, room.index);
    const list = inRoom.get(key) ?? [];
    list.push(radiator);
    inRoom.set(key, list);
  }

  const scenes = new Map<number, FitScene>();
  const sceneFor = (story: number): FitScene => {
    let scene = scenes.get(story);
    if (!scene) {
      scene = buildFitScene(rooms.filter((r) => r.story === story));
      scenes.set(story, scene);
    }
    return scene;
  };

  const out: RoomDesignCheck[] = rooms.map((room) =>
    checkRoom(room, inRoom.get(roomKey(room.story, room.index)) ?? [], {
      heatLoss,
      temps,
      maxUpgrades,
      objects: objects.filter((o) => o.storey === room.story),
      scene: () => sceneFor(room.story),
    }),
  );

  let worstRoom: RoomsCheck["worstRoom"];
  const assessed = out.filter((r) => r.requiredFlowTempC !== undefined);
  const withRadiators = out.filter((r) => r.radiators.length > 0 && r.heatLossRoom !== undefined);
  for (const r of assessed) {
    if (!worstRoom || (r.requiredFlowTempC as number) > worstRoom.requiredFlowTempC) {
      worstRoom = {
        name: r.name,
        story: r.story,
        index: r.index,
        requiredFlowTempC: r.requiredFlowTempC as number,
        complete: true,
      };
    }
  }
  if (worstRoom) {
    const complete =
      assessed.length === withRadiators.length && out.every((r) => r.heatLossRoom !== undefined || r.radiators.length === 0);
    worstRoom = { ...worstRoom, complete };
  }

  return { rooms: out, unplaced, ...(worstRoom ? { worstRoom } : {}) };
}

interface RoomContext {
  readonly heatLoss: DesignCheckHeatLoss;
  readonly temps: DesignWaterTemps | undefined;
  readonly maxUpgrades: number;
  /** The room's storey's objects. */
  readonly objects: readonly PlanObjectOnPlan[];
  readonly scene: () => FitScene;
}

function checkRoom(room: PlanRoom, radiators: readonly Radiator[], ctx: RoomContext): RoomDesignCheck {
  const base = {
    story: room.story,
    index: room.index,
    scanLabel: room.label,
    ...(room.externalRef !== undefined ? { externalRef: room.externalRef } : {}),
  };
  const noOutputs: RadiatorOutput[] = radiators.map((r) => ({
    objectId: r.id,
    label: radiatorLabel(r.spec),
    working: [],
  }));

  if (!ctx.heatLoss.available) {
    return {
      ...base,
      name: room.label,
      state: "unknown",
      message: `${room.label}: no heat loss for this room — ${ctx.heatLoss.reason}`,
      notes: [],
      radiators: noOutputs,
      upgrades: [],
    };
  }
  const match =
    room.externalRef !== undefined
      ? ctx.heatLoss.rooms.find((r) => r.externalRef === room.externalRef)
      : undefined;
  if (!match) {
    return {
      ...base,
      name: room.label,
      state: "unknown",
      message:
        `${room.label}: no heat loss for this room — the survey has no room recorded for this scanned room, ` +
        "so its radiators cannot be checked. Nothing is estimated from the scan.",
      notes: [],
      radiators: noOutputs,
      upgrades: [],
    };
  }

  const name = match.name;
  const load = match.heatLossW;
  if (!ctx.temps) {
    return {
      ...base,
      heatLossRoom: match,
      name,
      state: "unknown",
      message: `${name} needs ${formatW(load)}. Set the design flow temperature to check its radiators.`,
      notes: [],
      radiators: noOutputs,
      upgrades: [],
    };
  }
  const temps = ctx.temps;
  const at = `${formatC(temps.flowC)} flow`;
  const roomTemp = match.designTempC;
  const notes: string[] = [];
  if (temps.deltaTAssumed) {
    notes.push(
      `Return taken as ${formatC(temps.returnC)}: a ${temps.systemDeltaTK} K flow–return difference is assumed (the design pack's figure) until you set yours.`,
    );
  }

  const smallestSingle = smallestSingleRadiator(load, temps, roomTemp);

  if (radiators.length === 0) {
    return {
      ...base,
      heatLossRoom: match,
      name,
      state: "unknown",
      message:
        `${name} needs ${formatW(load)} at ${at} — no radiator on the plan in this room. ` +
        "Place one, or record how the room is heated if it is not by a new radiator.",
      notes,
      radiators: [],
      upgrades: [],
      ...(smallestSingle ? { smallestSingle } : {}),
    };
  }

  const outputs = radiators.map((r) => radiatorOutput(r, temps, roomTemp));
  const unknown = outputs.filter((o) => o.outputW === undefined);
  if (unknown.length > 0) {
    return {
      ...base,
      heatLossRoom: match,
      name,
      state: "unknown",
      message: `${name}: ${unknown.map((u) => u.unknownReason).join(" ")}`,
      notes,
      radiators: outputs,
      upgrades: [],
    };
  }

  const total = outputs.reduce((sum, o) => sum + (o.outputW as number), 0);
  const fraction = load > 0 ? total / load : Infinity;
  // Compared at the engine's own precision (both figures are rounded to 1e-6 W
  // where they are made), so "exactly 100%" is 100%, not a hair either side.
  const meets = round6(total) >= round6(load);
  const requiredFlowTempC = flowTempForRadiators(
    outputs.map((o) => ({
      ratedOutputW: o.ratedOutputW as number,
      ratedDeltaTK: o.ratedDeltaTK as number,
      exponent: o.exponent as number,
    })),
    load,
    roomTemp,
    temps.systemDeltaTK,
  );
  const giveOrGives = outputs.length === 1 ? "radiator gives" : `${outputs.length} radiators give`;
  const message = meets
    ? `${name}: ${giveOrGives} ${formatW(total)} at ${at}; room needs ${formatW(load)} — ${load > 0 ? formatPercent(fraction) : "no heat loss"}. Meets it.`
    : `${name}: ${giveOrGives} ${formatW(total)} at ${at}; room needs ${formatW(load)} — ${formatPercent(fraction, { failing: true })}. Too small by ${formatW(load - total)}.`;

  if (roomTemp >= (temps.flowC + temps.returnC) / 2) {
    notes.push(
      `The mean water temperature (${formatC((temps.flowC + temps.returnC) / 2)}) is not above the room's ${formatC(roomTemp)}: no radiator can heat it at this flow temperature.`,
    );
  }
  if (requiredFlowTempC !== undefined) {
    notes.push(
      meets
        ? `These radiators would still cover the room down to ${formatC(requiredFlowTempC)} flow.`
        : `These radiators would need ${formatC(requiredFlowTempC)} flow to cover the room.`,
    );
  }
  notes.push(`Radiator outputs: ${RADIATOR_OUTPUT_SOURCE}.`);

  let upgrades: RadiatorUpgrade[] = [];
  let addRadiatorNote: string | undefined;
  if (!meets) {
    upgrades = findUpgrades(radiators, outputs, load, total, temps, roomTemp, ctx);
    if (upgrades.length === 0) {
      addRadiatorNote =
        `No single ${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range} size both covers ${name} and fits where its radiator is — ` +
        `add a second radiator (the room is ${formatW(load - total)} short at ${at}), move it to a longer wall, or raise the flow temperature.`;
    }
  }

  return {
    ...base,
    heatLossRoom: match,
    name,
    state: meets ? "pass" : "fail",
    message,
    notes,
    radiators: outputs,
    totalOutputW: total,
    fraction,
    shortfallW: Math.max(load - total, 0),
    ...(requiredFlowTempC !== undefined ? { requiredFlowTempC } : {}),
    upgrades,
    ...(addRadiatorNote ? { addRadiatorNote } : {}),
    ...(!meets && smallestSingle ? { smallestSingle } : {}),
  };
}

function smallestSingleRadiator(
  loadW: number,
  temps: DesignWaterTemps,
  roomTempC: number,
): SingleRadiatorOption | undefined {
  let best: SingleRadiatorOption | undefined;
  for (const entry of CATALOGUE.data.entries) {
    if (entry.panelType === undefined) continue;
    const output = outputOf(entry, temps, roomTempC);
    if (output === undefined || output < loadW) continue;
    const spec = radiatorSpecFor(entry.panelType, entry.heightMm, entry.lengthMm);
    if (!spec) continue;
    if (!best || output < best.outputW) {
      best = { spec, label: radiatorLabel(spec), outputW: output };
    }
  }
  return best;
}

/**
 * Bigger sizes that close the gap and fit. For each radiator in the room, the
 * catalogue sizes that would lift the room to 100%, in three groups — the same
 * type and height (a longer one), another type at the same height (more
 * panels), then another height — and from each group the smallest that
 * `fittingPlacement` can place so it passes every fit check. So the list is
 * varied ("longer", "deeper", "taller") rather than three lengths of one.
 */
function findUpgrades(
  radiators: readonly Radiator[],
  outputs: readonly RadiatorOutput[],
  load: number,
  total: number,
  temps: DesignWaterTemps,
  roomTempC: number,
  ctx: RoomContext,
): RadiatorUpgrade[] {
  const found: RadiatorUpgrade[] = [];
  radiators.forEach((radiator, i) => {
    const current = outputs[i]!;
    const others = total - (current.outputW ?? 0);
    const rank = (e: RadiatorCatalogueEntry): number =>
      e.heightMm === radiator.spec.heightMm ? (e.panelType === radiator.spec.panelType ? 0 : 1) : 2;
    const candidates = CATALOGUE.data.entries
      .filter((e) => e.panelType !== undefined && e.ratedOutputW > (current.ratedOutputW ?? 0))
      .map((e) => ({ entry: e, output: outputOf(e, temps, roomTempC) }))
      .filter(
        (c): c is { entry: RadiatorCatalogueEntry; output: number } =>
          c.output !== undefined && others + c.output >= load,
      )
      .sort((a, b) => a.entry.ratedOutputW - b.entry.ratedOutputW);
    for (const group of [0, 1, 2]) {
      for (const { entry, output } of candidates.filter((c) => rank(c.entry) === group)) {
        const spec = radiatorSpecFor(entry.panelType!, entry.heightMm, entry.lengthMm);
        if (!spec) continue;
        const geometry = fittingPlacement(radiator, spec, ctx.objects, ctx.scene());
        if (!geometry) continue;
        const roomTotal = others + output;
        found.push({
          objectId: radiator.id,
          spec,
          geometry,
          label: `Use ${radiatorLabel(spec)} — room ${formatW(roomTotal)}, ${formatPercent(roomTotal / load)}, fits`,
          outputW: output,
          roomTotalW: roomTotal,
          fraction: roomTotal / load,
        });
        break;
      }
    }
  });
  // In group order — longer, then more panels, then taller — as found.
  return found.slice(0, ctx.maxUpgrades);
}
